import type { StagedActionEditOptions, StagedActionViewTag, UpdateStagedActionView } from './use-staged-actions'

/**
 * Pure optimistic reducer for the `tagEdits` portion of a view update. Returns
 * the next `view.tags` array given the current tags, the requested edits, and
 * the edit-option metadata that describes each editable tag.
 *
 * Tags whose committed value is a freshly-serialized payload rather than a fixed
 * option (`multiText`, `dateTime`) are left untouched: their value never matches
 * an `editTag.options[*].value`, so an optimistic rewrite would silently DROP
 * the existing chips. We leave those chips in place and let `onSuccess` repaint
 * them from the server's normalized tags. This is the regression-prone path —
 * see use-update-staged-action-view.test.ts.
 *
 * Kept in its own module (free of fetch/IPC imports) so it stays unit-testable
 * without dragging the renderer-only API client into the test runtime.
 */
export function applyOptimisticTagEdits(
  currentTags: Array<StagedActionViewTag>,
  tagEdits: NonNullable<UpdateStagedActionView['tagEdits']>,
  editTags: NonNullable<StagedActionEditOptions['tags']>,
): Array<StagedActionViewTag> {
  let tags = [...currentTags]
  for (const [key, values] of Object.entries(tagEdits)) {
    const editTag = editTags.find((et) => et.key === key)

    // No optimistic rewrite for multiText/dateTime tags: their committed value
    // is a freshly-serialized payload that never matches a fixed editTag option,
    // so a rewrite would drop the existing chips. Leave the existing tags
    // untouched and let onSuccess repaint them from the server's normalized tags.
    if (editTag?.inputType === 'multiText' || editTag?.inputType === 'dateTime') continue

    tags = tags.filter((t) => t.key !== key)
    if (editTag) {
      if (editTag.inputType === 'localFile') {
        for (const value of values) {
          tags.push({ key, value })
        }
      } else {
        for (const value of values) {
          const opt = editTag.options.find((o) => o.value === value)
          if (opt) {
            tags.push({ key, value: opt.name, icon: opt.icon })
          }
        }
      }
    }
  }
  return tags
}
